import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import {
  House, ShoppingCart, FileText, Users, Buildings, Receipt, Storefront,
  ChartLineUp, Sun, Moon, SignOut, Package, UserCircle, List
} from "@phosphor-icons/react";
import { useState } from "react";

const ROLE_LABEL = {
  super_user: "Super User",
  partner_success_manager: "Partner Success Manager",
  partner_manager: "Partner Manager",
  partner_user: "Partner User",
};

function navFor(role) {
  const base = [
    { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
    { to: "/store", label: "Store", icon: Storefront, testid: "nav-store" },
    { to: "/quotes", label: "Quotes", icon: FileText, testid: "nav-quotes" },
    { to: "/leads", label: "Leads", icon: ChartLineUp, testid: "nav-leads" },
    { to: "/orders", label: "Orders", icon: Receipt, testid: "nav-orders" },
  ];
  if (role === "super_user") {
    return [
      ...base,
      { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
      { to: "/admin/partners", label: "Partners", icon: Buildings, testid: "nav-partners" },
      { to: "/admin/products", label: "Products", icon: Package, testid: "nav-products" },
    ];
  }
  if (role === "partner_success_manager") {
    return [
      ...base,
      { to: "/admin/partners", label: "My Partners", icon: Buildings, testid: "nav-partners" },
      { to: "/admin/users", label: "Partner Users", icon: Users, testid: "nav-users" },
    ];
  }
  if (role === "partner_manager") {
    return [
      ...base,
      { to: "/admin/users", label: "Team", icon: Users, testid: "nav-users" },
    ];
  }
  return base;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = navFor(user?.role || "partner_user");

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card" data-testid="sidebar">
        <Link to="/dashboard" className="px-6 py-5 border-b border-border" data-testid="sidebar-logo">
          <div className="font-display font-black text-lg tracking-tight">UC GTM App</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Partner Portal</div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-[var(--uc-primary)] text-white" : "text-foreground/80 hover:bg-muted"
                }`
              }>
              <Icon size={18} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-border space-y-2">
          <div className="px-3 py-2 text-xs">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-muted-foreground truncate">{ROLE_LABEL[user?.role]}</div>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileOpen(false)}>
          <aside className="w-64 h-full bg-card border-r border-border p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-black mb-3">UC GTM App</div>
            {nav.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm ${isActive ? "bg-[var(--uc-primary)] text-white" : "hover:bg-muted"}`
                }>
                <Icon size={18} weight="duotone" />{label}
              </NavLink>
            ))}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-14 border-b border-border bg-card/70 backdrop-blur-xl px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <List size={22} />
            </button>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">
              {ROLE_LABEL[user?.role]}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/cart")} className="relative p-2 rounded-sm hover:bg-muted" data-testid="header-cart">
              <ShoppingCart size={20} weight="duotone" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 text-[10px] bg-[var(--uc-accent)] text-white rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
            <button onClick={toggle} className="p-2 rounded-sm hover:bg-muted" data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-border ml-1">
              <UserCircle size={22} weight="duotone" />
              <div className="text-xs">
                <div className="font-medium">{user?.name}</div>
                <div className="text-muted-foreground text-[10px]">{user?.email}</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn" className="rounded-sm">
              <SignOut size={16} className="mr-1" /> Sign out
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="app-main">{children}</main>
      </div>
    </div>
  );
}
